import {
  Box,
  Card,
  Group,
  SegmentedControl,
  Text,
  TextInput,
  useComputedColorScheme,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { BarChart } from "@/viz/BarChart";
import {
  ClusterMap,
  type ClusterStatus,
  type GeoJsonCollection,
  statusLabel,
} from "@/viz/ClusterMap";
import { DataTable } from "@/viz/DataTable";
import { PieChart } from "@/viz/PieChart";
import { mantineColor } from "@/viz/tokens";

interface Props {
  title: string;
  subtitle?: string;
  /** Which mark draws the chart view; the table view is identical either way. */
  form?: "bar" | "pie" | "map";
  keys: string[];
  values: (number | null)[];
  keyLabel: string;
  valueLabel: string;
  /** True while refetching: hold the previous render rather than flashing. */
  isRefreshing?: boolean;
  formatValue?: (v: number) => string;
  footnote?: string;
  /** Bar only — forwarded to `BarChart`. */
  thresholds?: { min: number; color: string }[];
  /** Map only — the composed FeatureCollection from the geometry endpoint. */
  geojson?: GeoJsonCollection;
  /** Map only — one status per key, aligned to `keys`. */
  statuses?: (string | null)[];
  /** Map only — declared order is legend order. */
  statusStyles?: ClusterStatus[];
  /** Map only. */
  mapHeight?: number;
  /** Map only. */
  mapLabels?: boolean;
}

/**
 * A chart and its table twin behind one toggle.
 *
 * The toggle is what makes the tooltip an enhancement rather than the only
 * route to a value.
 */
export function ChartCard({
  title,
  subtitle,
  form = "bar",
  keys,
  values,
  keyLabel,
  valueLabel,
  isRefreshing = false,
  formatValue,
  footnote,
  thresholds,
  geojson,
  statuses,
  statusStyles,
  mapHeight,
  mapLabels = false,
}: Props) {
  const [view, setView] = useState<"chart" | "table">("chart");
  const [search, setSearch] = useState("");
  const scheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  // Client-side: the table view already holds the whole (limited) result set,
  // so narrowing it further costs nothing and needs no round trip.
  const needle = search.trim().toLowerCase();
  const filteredKeys = needle
    ? keys.filter((key) => key.toLowerCase().includes(needle))
    : keys;
  // One index built once, not `keys.indexOf` per row: the latter is O(n) per
  // lookup, making a `.map()` over filtered keys O(n²) — up to a million
  // comparisons per keystroke at 300-1000 clusters. Memoized on `keys` alone
  // so bar/pie chart-view renders — which never read this — don't rebuild it.
  const indexByKey = useMemo(() => new Map(keys.map((key, i) => [key, i])), [keys]);

  return (
    <Card withBorder padding="lg" h="100%">
      <Group justify="space-between" align="flex-start" mb={subtitle ? 4 : "md"}>
        <Text fw={600}>{title}</Text>
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as "chart" | "table")}
          data={[
            { label: "Chart", value: "chart" },
            { label: "Table", value: "table" },
          ]}
        />
      </Group>

      {subtitle && (
        <Text size="sm" c="dimmed" mb="md">
          {subtitle}
        </Text>
      )}

      {/* Dim in place instead of unmounting for a skeleton — no layout jump. */}
      <div
        style={{
          opacity: isRefreshing ? 0.55 : 1,
          transition: "opacity 150ms ease",
        }}
      >
        {keys.length === 0 ? (
          <Text size="sm" c="dimmed" py="xl" ta="center">
            No data
          </Text>
        ) : view === "table" ? (
          <>
            <TextInput
              size="sm"
              mb="sm"
              maw={260}
              placeholder={`Search ${keyLabel}`}
              leftSection={<IconSearch size={15} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <DataTable
              columns={
                statuses ? [keyLabel, "Status", valueLabel] : [keyLabel, valueLabel]
              }
              rows={filteredKeys.map((key) => {
                const i = indexByKey.get(key)!;
                const status = statuses?.[i] ?? null;
                const label = statusLabel(status, statusStyles ?? []);
                return statuses ? [key, label, values[i]] : [key, values[i]];
              })}
              formatValue={formatValue}
            />
          </>
        ) : form === "map" ? (
          geojson ? (
            <ClusterMap
              geojson={geojson}
              keys={keys}
              values={values}
              statuses={statuses ?? []}
              statusStyles={statusStyles ?? []}
              height={mapHeight ?? 560}
              labels={mapLabels}
              valueLabel={valueLabel.toLowerCase()}
            />
          ) : (
            // A map whose geometry failed still has data, and the table twin
            // is the accessible path to it (§6) — so this branch keeps the
            // card and its toggle rather than replacing both with an error.
            // The footnote names the cause.
            <Text size="sm" c="dimmed" py="xl" ta="center">
              The map could not be drawn. Switch to Table to read the values.
            </Text>
          )
        ) : form === "pie" ? (
          <PieChart
            keys={keys}
            values={values}
            valueLabel={valueLabel}
            formatValue={formatValue}
          />
        ) : (
          <BarChart
            keys={keys}
            values={values}
            valueLabel={valueLabel}
            formatValue={formatValue}
            thresholds={thresholds}
          />
        )}
      </div>

      {form === "map" && statusStyles && statuses && (
        <Group gap="lg" mt="sm">
          {statusStyles.map((style) => (
            <Group key={style.value} gap={6}>
              <Box
                w={10}
                h={10}
                style={{
                  borderRadius: 2,
                  background: mantineColor(style.color, scheme),
                  opacity: 0.65,
                }}
              />
              <Text size="xs" c="dimmed">
                {style.label} {statuses.filter((s) => s === style.value).length}
              </Text>
            </Group>
          ))}
        </Group>
      )}

      {footnote && (
        <Text size="xs" c="dimmed" mt="sm">
          {footnote}
        </Text>
      )}
    </Card>
  );
}
